// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
'use client'

import { CareersFormValues } from 'app/careers/(positions)/CareersFormValues'
import { submitForm } from 'app/careers/(positions)/actions.client'
import { Interest } from 'app/careers/(positions)/enums/Interests.enum'
import { SeniorityLevel } from 'app/careers/(positions)/enums/SeniorityLevel.enum'
import CareersPositionVM, { PositionTypeEnum } from 'clients/strapi/models/CareersPositionVM'
import Button from 'components/Buttons/Button/Button'
import DropzoneInput from 'components/Form/DropzoneInput/DropzoneInput'
import Input from 'components/Form/Input/Input'
import Textarea from 'components/Form/Textarea/Textarea'
import Checkboxes from 'components/FormSections/Checkboxes/Checkboxes'
import TurnstileButton from 'components/FormSections/TurnstileButton/TurnstileButton'
import { emailRegex, MAX_UPLOAD_FILES_NUMBER } from 'constants/global'
import paths from 'constants/paths'
import { motion } from 'motion/react'
import Link from 'next/link'
import Success from 'public/assets/careers/success.svg'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import styles from './PositionForm.module.scss'

type PreSubmitCareersFormValues = {
  positionId: number
  fullName: string
  email: string
  about: string
  interests: Interest[]
  level: SeniorityLevel
  files: File[]
  token: string
}

type Props = {
  position?: CareersPositionVM
}

const PositionForm = ({ position }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [turnstileFailed, setTurnstileFailed] = useState(false)
  const { handleSubmit, setValue, control } = useForm<PreSubmitCareersFormValues>()
  const [formSubmitted, setFormSubmitted] = useState(false)

  const onSubmit = async (data: PreSubmitCareersFormValues) => {
    setIsLoading(true)
    const formValues: CareersFormValues = {
      ...data,
      positionId: position?.id,
      positionName: position?.title,
      interests: position?.type === PositionTypeEnum.Open ? data.interests.join(', ') : '',
      level: position?.type === PositionTypeEnum.Open ? data.level.toString() : '',
    }
    const res = await submitForm(formValues)
    setIsLoading(false)

    if (res) {
      setFormSubmitted(true)
      document.getElementById('scroll-anchor').scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <motion.form layoutRoot layout className={styles.container} onSubmit={handleSubmit(onSubmit)}>
      <span className={styles.pixel + ' ' + styles.first}></span>
      <span className={styles.pixel + ' ' + styles.second}></span>

      <motion.div layout='position' className={styles['form-title']}>
        <span className={styles.title}>Apply here!</span>
        <p id='scroll-anchor' className={styles.text}>
          Join our team! Complete the form, submit your resume, and we’ll be in touch soon.
        </p>
      </motion.div>

      {!formSubmitted ? (
        <div className={styles['form-content']}>
          <section className={styles.section}>
            <p className={styles.subtitle}>How can we contact you?</p>
            <div className={styles.fields}>
              <Input
                label='Full name'
                type='text'
                control={control}
                name='fullName'
                rules={{
                  required: {
                    value: true,
                    message: 'Please fill in your full name.',
                  },
                }}
              />
              <Input
                label='E-mail'
                type='email'
                control={control}
                name='email'
                rules={{
                  required: {
                    value: true,
                    message: 'Please fill in your E-mail address.',
                  },
                  pattern: {
                    value: emailRegex,
                    message: 'E-mail must be formatted correctly.',
                  },
                }}
              />
            </div>
          </section>
          <section className={styles.section}>
            <p className={styles.subtitle}>Tell us about yourself.</p>
            <Textarea
              label='Share anything you’d like us to know!'
              maxCharacters={1000}
              control={control}
              name='about'
              rules={{
                required: {
                  value: true,
                  message: 'Please tell us something about yourself.',
                },
                maxLength: {
                  value: 1000,
                  message: '1000 characters max.',
                },
              }}
            />
          </section>
          <DropzoneInput
            name='files'
            multiple={true}
            placeholder='your CV, portfolio, and any other documents you feel is relevant'
            maxFiles={MAX_UPLOAD_FILES_NUMBER}
            control={control}
          />
          {position?.type === PositionTypeEnum.Open && (
            <>
              <Checkboxes
                subtitle='What roles interest you?'
                name='interests'
                options={Interest}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Please choose at least one interest.',
                  },
                  validate: (value: string[]) => value.filter((x) => x !== '').length >= 1,
                }}
              />
              <Checkboxes
                subtitle="What's your level of expertise?"
                name='level'
                options={SeniorityLevel}
                type='radio'
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Please choose your seniority level.',
                  },
                }}
              />
            </>
          )}
          <TurnstileButton
            control={control}
            rules={{
              required: true,
              validate: (value: string) => value.length > 0,
            }}
            setValue={setValue}
            hasFailed={(b) => setTurnstileFailed(b)}
          />
          <Button type='submit' disabled={isLoading || turnstileFailed}>
            Submit
          </Button>
        </div>
      ) : (
        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} transition={{ duration: 0.3, ease: 'linear' }} className={styles['form-submitted']}>
          <Success />
          <div className={styles['success-text']}>
            <span className={styles.big}>We&apos;ve received your application!</span>
            <span className={styles.small}>We&apos;ll process it and reach out if it fits the bill</span>
          </div>
          <Link href={paths.about} prefetch={false}>
            <Button>Read More About Us</Button>
          </Link>
        </motion.div>
      )}
    </motion.form>
  )
}

export default PositionForm
